import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DynForm} from '../../domain/meta/dyn-form';
import {Observable} from 'rxjs/Observable';
import {MatPaginator, MatSort, MatTableDataSource} from '@angular/material';
import {FormService} from '../../services/form.service';
import {AuthService} from '../../../core/security/auth.service';
import {Router} from '@angular/router';
import {ConfirmationService} from 'primeng/primeng';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {GlobalAppService} from '../../../core/commons/global-app.service';
import {MessageSeverity} from '../../../core/commons/message-severity';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/observable/fromEvent';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {Ministry} from 'app/profile/domain/ministry/ministry';
import {MinistryService} from 'app/profile/service/ministry.service';
import {QueryOptions} from 'app/core/commons/query.options';
import {PageStateService} from '../../../core/service/page-state.service';
import {filter, take} from 'rxjs/operators';

@Component({
    selector: 'app-forms',
    templateUrl: './form-list.component.html',
    styleUrls: ['./form-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormListComponent implements OnInit, OnDestroy {
    // Forms
    formsDataSource = new MatTableDataSource([]);
    queryOptions: QueryOptions = new QueryOptions();
    resultsLength: number;
    formsData: any = {};

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;
    displayedColumns = [];

    formGroup: FormGroup;
    private reload = new BehaviorSubject<boolean>(false);

    /*
     * Filters
     */
    public ministries = [];
    filteredOptions: Observable<Ministry[]>;
    states = [
        {viewValue: 'Todos', value: ''},
        {viewValue: 'Publicado', value: 'PUBLISHED'},
        {viewValue: 'Deshabilitado', value: 'DISABLED'}
    ];

    constructor(
        private authService: AuthService,
        private formService: FormService,
        private ministryService: MinistryService,
        private router: Router,
        private confirmationService: ConfirmationService,
        private appService: GlobalAppService,
        private fb: FormBuilder,
        private pageStateService: PageStateService,
        private cd: ChangeDetectorRef
    ) {

        this.formGroup = this.fb.group({
            reference: new FormControl(''),
            name: new FormControl(''),
            ministry: new FormControl(''),
            state: new FormControl('PUBLISHED'),
            subform: new FormControl(false)
        });

    }

    ngOnInit(): void {
        this.pageStateService.setInitialPage();
        const stateObservable = this.pageStateService.loadState();

        stateObservable.pipe(filter(state => state != null))
            .subscribe(page => {
                //console.log("loading state...");
                this.formGroup.patchValue({
                    ministry : page.state.ministry,
                    name : page.state.nameValue,
                    state: page.state.stateValue,
                    subform: page.state.subformValue
                });
                this.queryOptions.page = page.state.page;
                this.queryOptions.size = page.state.size;
                this.paginator.pageIndex = page.state.page;
                this.paginator.pageSize = page.state.size;
                this.applyFilter();
            });

        stateObservable.pipe(filter(state => state == null))
            .subscribe(emptyState => {
                this.applyFilterAndReset();
            });

        this.authService.user.subscribe(user => {
            this.displayedColumns = user.hasFx('Form.CREATE')
                ? [
                    'referenceNumber',
                    'name',
                    'description',
                    'validSince',
                    'validUntil',
                    'subform',
                    'ministry',
                    'state',
                    'actions'
                ]
                : ['referenceNumber', 'name', 'description','ministry', 'actions'];
        });

        this.ministryService.getAll()
            .pipe(
                take(1)
            )
            .subscribe(ministries => {
                this.ministries = ministries;
            });


        this.formGroup.controls['ministry'].valueChanges.subscribe(() => {
            if (this.formGroup.controls['ministry'].value == '') {
                this.filteredOptions = this.getAllMinistries();
            } else {
                this.filteredOptions = this._filter(
                    this.formGroup.controls['ministry'].value
                );
            }
        });

        this.paginator.page.subscribe(() => {
            this.queryOptions.page = this.paginator.pageIndex;
            this.queryOptions.size = this.paginator.pageSize;
            this.applyFilter();
        });

    }

    public navigateToView(form: DynForm) {
        this.router.navigate(['home', 'forms', 'view', form.id, 'preview']);
    }

    public update(form: DynForm) {
        this.router.navigate(['home', 'forms', form.id]);
    }

    public fill(form: DynForm) {
        this.router.navigate(['home', 'forms', 'data', 'new', form.id]);
    }

    public delete(form: DynForm) {
        this.confirmationService.confirm({
            message: '¿Está seguro que desea eliminar el formulario?',
            icon: 'fa ui-icon-delete-forever',
            accept: () => {
                this.formService
                    .delete(form)
                    .do(() => this.appService.setLoading(true))
                    .subscribe(
                        () => {
                            this.appService.setLoading(false);
                            this.appService.sendMsg(
                                MessageSeverity.SUCCESS,
                                'Eliminado',
                                'Se ha eliminado el formulario.'
                            );
                            this.reload.next(true);
                        },
                        e => {
                            this.appService.setLoading(false);
                            this.appService.sendMsg(
                                MessageSeverity.ERROR,
                                'Error',
                                'No ha sido posible eliminar el formulario.' + e.json().message
                            );
                        }
                    );
            }
        });
    }

    createNewVersion(form: DynForm) {
        this.router.navigate(['home', 'forms', 'upgrade', form.getId()]);
    }

    createNewForm() {
        this.router.navigate(['home', 'forms', 'new']);
    }

    createFromTemplane(form: DynForm) {
        this.router.navigate(['home', 'forms', 'new', form.getId()]);
    }

    publish(form: DynForm) {
        this.confirmationService.confirm({
            message:
                '¿Esta seguro de publicar este formulario? La operación es irreversible.',
            icon: 'warning',
            accept: () => {
                this.appService.setLoading(true);
                this.formService.publish(form).subscribe(
                    f => {
                        this.appService.setLoading(false);
                        this.appService.sendMsg(
                            MessageSeverity.SUCCESS,
                            'Publicado',
                            'El formulario se ha publicado con exito.'
                        );
                        this.reload.next(true);
                    },
                    e => {
                        this.appService.setLoading(false);
                        this.appService.sendMsg(
                            MessageSeverity.ERROR,
                            'Error',
                            'No ha sido posible publicar el formulario. ' + e.json().message
                        );
                    }
                );
            }
        });
    }

    disable(form: DynForm) {
        this.confirmationService.confirm({
            message: '¿Esta seguro de deshabilitar este formulario?',
            icon: 'warning',
            accept: () => {
                this.appService.setLoading(true);
                this.formService.disable(form).subscribe(
                    f => {
                        this.appService.setLoading(false);
                        this.appService.sendMsg(
                            MessageSeverity.SUCCESS,
                            'Desabilitado',
                            'El formulario se ha deshabilitado con exito.'
                        );
                        this.reload.next(true);
                    },
                    e => {
                        this.appService.setLoading(false);
                        this.appService.sendMsg(
                            MessageSeverity.ERROR,
                            'Error',
                            'No ha sido posible deshabilitar el formulario. ' +
                            e.json().message
                        );
                    }
                );
            }
        });
    }

    /*
     *  Métodos para filtrar por ministerios.
     */
    private getAllMinistries(): Observable<Ministry[]> {
        return Observable.of(this.ministries);
    }

    displayFn(ministry?: Ministry): string | undefined {
        return ministry ? ministry.name : undefined;
    }

    private _filter(value: string): Observable<Ministry[]> {
        const filterValue = value.toLowerCase();

        return Observable.of(
            this.ministries.filter(option =>
                option.name.toLowerCase().includes(filterValue)
            )
        );
    }

    isClearIconNameVisible(): boolean {
        return this.formGroup.controls['name'].value != '';
    }

    clearInputName() {
        this.formGroup.patchValue({
            name: ''
        });
    }

    isClearIconMinistryVisible(): boolean {
        return this.formGroup.controls['ministry'].value != '';
    }

    clearInputMinistry() {
        this.formGroup.patchValue({
            ministry: ''
        });
    }

    isClearIconReferenceVisible(): boolean {
        return this.formGroup.controls['reference'].value != '';
    }

    clearInputReference() {
        this.formGroup.patchValue({
            reference: ''
        });
    }

    /*
     *  Métodos para aplicar y limpiar filtros
     */
    clearFilter() {
        this.formGroup.patchValue({
            reference: '',
            name: '',
            ministry: '',
            state: null,
            subform: false
        });
        this.applyFilterAndReset();
    }

    applyFilterAndReset() {
        this.paginator.pageIndex = 0;
        this.queryOptions.size = 5;
        this.queryOptions.page = 0;
        this.applyFilter();
    }

    applyFilter() {
        let ministryValue = this.formGroup.controls['ministry'].value.id;
        let nameValue = this.formGroup.controls['name'].value;
        let referenciaValue = this.formGroup.controls['reference'].value;
        let stateValue = '';
        if (
            this.formGroup.controls['state'].value != 'Todos' ||
            this.formGroup.controls['state'].value != null
        )
            stateValue = this.formGroup.controls['state'].value;

        let subformValue = this.formGroup.controls['subform'].value;
        this.formService
            .pagedSearch(
                referenciaValue,
                nameValue,
                ministryValue,
                stateValue,
                subformValue,
                this.queryOptions
            )
            .subscribe(data => {
                this.formsData = data;
                this.formsDataSource.data = this.formsData.content;
                this.resultsLength = this.formsData.totalElements;
            });
        this.cd.markForCheck();
    }

    isDataToClear() {
        if (
            this.formGroup.controls['ministry'].value.id != null ||
            this.formGroup.controls['reference'].value != '' ||
            this.formGroup.controls['name'].value != '' ||
            this.formGroup.controls['state'].value != null ||
            this.formGroup.controls['subform'].value != false
        )
            return true;
        return false;
    }

    ngOnDestroy(): void {
        let pageState = {
            ministry: this.formGroup.controls['ministry'].value,
            nameValue: this.formGroup.controls['name'].value,
            stateValue: this.formGroup.controls['state'].value,
            subformValue: this.formGroup.controls['subform'].value,
            page: this.queryOptions.page,
            size: this.queryOptions.size
        };

        this.pageStateService.saveState(pageState);
    }

}



// WEBPACK FOOTER //
// ./src/app/forms/components/form-list/form-list.component.ts